import { useModules } from '../lib/modules-context';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LineChart, TrendingUp, HardHat, Lock, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const MODULE_INFO = {
  financial: {
    name: 'Financial Intelligence',
    icon: LineChart,
    color: 'var(--financial-primary)',
    light: 'var(--financial-light)',
    description: 'Motor de análisis financiero profesional para desarrollo inmobiliario.',
    features: ['TIR, VAN, ROI, MOIC y 18+ indicadores', 'Flujo de caja mensual detallado', 'Análisis de sensibilidad y tornado', 'Comparación de escenarios'],
    path: '/analyzer',
  },
  market: {
    name: 'Market Intelligence',
    icon: TrendingUp,
    color: 'var(--market-primary)',
    light: 'var(--market-light)',
    description: 'Validación de mercado basada en comparables reales de tu zona.',
    features: ['Captura y análisis de comparables', 'Posicionamiento de precio vs mercado', 'Simulación de precio y absorción', 'Recomendaciones accionables'],
    path: '/market',
  },
  construction: {
    name: 'Construction Intelligence',
    icon: HardHat,
    color: 'var(--construction-primary)',
    light: 'var(--construction-light)',
    description: 'Monitoreo y control inteligente del avance de obra.',
    features: ['Seguimiento de avance vs presupuesto', 'Control de costos en tiempo real', 'Alertas de desviaciones', 'Reportes de avance de obra'],
    path: '/construction',
  },
};

/* ─── Module Gate ─── */
export function ModuleGate({ module, children }) {
  const { isActive, isTrial, trialEnds } = useModules();

  if (isActive(module)) {
    return (
      <>
        {isTrial && (
          <TrialBanner module={module} trialEnds={trialEnds} />
        )}
        {children}
      </>
    );
  }

  return <ModulePreview module={module} />;
}

/* ─── Trial Banner ─── */
function TrialBanner({ module, trialEnds }) {
  if (!trialEnds) return null;
  const daysLeft = Math.max(0, Math.ceil((new Date(trialEnds) - new Date()) / (1000 * 60 * 60 * 24)));
  if (daysLeft > 14) return null; // Only show when < 14 days left

  return (
    <div className="mx-6 mt-4 rounded-lg px-4 py-2 flex items-center gap-2 text-xs" style={{ background: 'var(--accent-subtle)', border: '1px solid rgba(99,91,255,0.15)' }} data-testid="trial-banner">
      <Sparkles className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--accent-primary)' }} />
      <span style={{ color: 'var(--text-secondary)' }}>
        <strong style={{ color: 'var(--accent-primary)' }}>Trial activo</strong> — {daysLeft} días restantes para {MODULE_INFO[module]?.name}
      </span>
    </div>
  );
}

/* ─── Module Preview (shown when module is inactive) ─── */
function ModulePreview({ module }) {
  const info = MODULE_INFO[module];
  if (!info) return null;
  const Icon = info.icon;

  return (
    <div className="h-full overflow-y-auto" data-testid={`module-preview-${module}`}>
      <div className="px-6 lg:px-8 py-8 max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: info.light }}>
              <Icon className="w-8 h-8" style={{ color: info.color }} />
            </div>
            <Badge className="text-[10px] mb-4" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)', border: 'none' }}>
              <Lock className="w-3 h-3 mr-1" /> Módulo no activo
            </Badge>
            <h1 className="font-display text-2xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{info.name}</h1>
            <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>{info.description}</p>
          </div>

          <Card className="rounded-xl border-border/50 shadow-sm mb-6">
            <CardContent className="p-6">
              <h3 className="text-xs font-display font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Qué incluye</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {info.features.map((f, i) => (
                  <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                    <div className="w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5" style={{ background: info.light }}>
                      <span className="text-[9px] font-bold" style={{ color: info.color }}>{i + 1}</span>
                    </div>
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{f}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <Button className="h-11 px-8 text-white font-semibold" style={{ background: info.color }}>
              Activar {info.name} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <p className="text-[11px] mt-3" style={{ color: 'var(--text-muted)' }}>Contacta a tu administrador o visita Configuración</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ─── Upsell Card (contextual suggestion inside other modules) ─── */
export function UpsellCard({ fromModule, toModule }) {
  const { isActive } = useModules();
  const navigate = useNavigate();

  if (isActive(toModule)) return null;

  const info = MODULE_INFO[toModule];
  if (!info) return null;
  const Icon = info.icon;

  const SUGGESTIONS = {
    'financial→market': { text: 'Valida tus supuestos con datos reales de mercado', sub: 'Compara precio y absorción contra proyectos similares de tu zona.' },
    'financial→construction': { text: 'Compara el desempeño real contra tu escenario financiero', sub: 'Monitorea avance de obra, costos reales y desviaciones vs presupuesto.' },
    'market→financial': { text: 'Simula el impacto de mercado en tu rentabilidad', sub: 'Ve cómo los hallazgos de mercado afectan TIR, VAN y flujo de caja.' },
    'market→construction': { text: 'Conecta velocidad de venta con avance de obra', sub: 'Correlaciona absorción real con el progreso de construcción.' },
    'construction→financial': { text: 'Ve el impacto de sobrecostos en la rentabilidad', sub: 'Conecta desviaciones de presupuesto con el análisis financiero.' },
    'construction→market': { text: 'Compara avance de ventas vs absorción de mercado', sub: 'Valida si tu velocidad de venta es consistente con el mercado.' },
    'copilot→market': { text: 'Valida tus supuestos con inteligencia de mercado', sub: 'Agrega comparables para que el Copilot te dé recomendaciones basadas en datos reales.' },
    'copilot→construction': { text: 'Monitorea la ejecución de tu proyecto', sub: 'El Copilot podrá analizar desviaciones de obra y su impacto financiero.' },
  };

  const content = SUGGESTIONS[`${fromModule}→${toModule}`] || { text: `Complementa tu análisis con ${info.name}`, sub: info.description };

  return (
    <div className="rounded-lg p-3.5 flex items-start gap-3" style={{ background: info.light, border: `1px solid ${info.color}15` }} data-testid={`upsell-${toModule}`}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${info.color}12` }}>
        <Icon className="w-4.5 h-4.5" style={{ color: info.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>{content.text}</div>
        <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{content.sub}</p>
      </div>
      <Button size="sm" className="shrink-0 text-[11px] h-7 text-white mt-0.5" style={{ background: info.color }} onClick={() => navigate(info.path)}>
        Desbloquear
      </Button>
    </div>
  );
}

export { MODULE_INFO };
